import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const BUCKET_NAME = 'post-media';
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const ALLOWED_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];

export interface UploadedMedia {
  storagePath: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export function useMediaUpload() {
  const { user } = useAuth();
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const uploadFile = async (file: File): Promise<UploadedMedia> => {
    if (!user?.id) throw new Error('User not authenticated');

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error('File type not supported. Please upload images (JPEG, PNG, GIF, WebP) or videos (MP4, MOV, WebM).');
    }

    // Validate file size
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) {
      throw new Error(`File too large. Maximum size is ${isVideo ? '50MB' : '10MB'}.`);
    }

    // Generate unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const storagePath = `${user.id}/${fileName}`;

    setUploadProgress(0);

    // Upload file
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    setUploadProgress(100);

    return {
      storagePath,
      fileName: file.name,
      fileType: file.type.startsWith('image/') ? 'image' : 'video',
      fileSize: file.size,
    };
  };

  const deleteFile = async (storagePath: string) => {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([storagePath]);

    if (error) throw error;
  };

  return {
    uploadFile,
    deleteFile,
    uploadProgress,
  };
}

export function useSavePostMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      postId,
      media,
    }: {
      postId: string;
      media: UploadedMedia[];
    }) => {
      const mediaRecords = media.map((m, index) => ({
        post_id: postId,
        storage_path: m.storagePath,
        file_name: m.fileName,
        file_type: m.fileType,
        file_size: m.fileSize,
        sort_order: index,
      }));

      const { data, error } = await supabase
        .from('post_media')
        .insert(mediaRecords)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function usePostMedia(postId: string) {
  const queryClient = useQueryClient();

  return {
    getMedia: async () => {
      const { data, error } = await supabase
        .from('post_media')
        .select('*')
        .eq('post_id', postId)
        .order('sort_order');

      if (error) throw error;
      return data;
    },
  };
}
